/**
 * Saudi store banner (sections/ksa-store-banner-custom.liquid).
 *
 * The bar renders hidden. This asks Shopify's /browsing_context_suggestions.json for the
 * visitor's detected country and reveals the bar only when it matches data-country (SA).
 * A dismissal is remembered in localStorage; if the lookup or storage fails, the bar
 * stays hidden or simply shows again next time. ?ksa_banner=SA forces the country, for
 * previews.
 *
 * GA4 gets `ksa_banner_view` when the bar is shown and `ksa_banner_click` on the link, sent
 * only to the property the Google & YouTube app configured (found in dataLayer), as in
 * assets/gallery-tracking-custom.js.
 */
if (!customElements.get('ksa-store-banner')) {
  const STORAGE_KEY = 'cc-ksa-banner-dismissed';

  /**
   * Returns the GA4 measurement id the Google & YouTube app configured, if it has loaded.
   * @returns {?string}
   */
  const ga4Id = () => {
    const entry = (window.dataLayer || []).find((args) => args && args[0] === 'config'
      && typeof args[1] === 'string' && args[1].startsWith('G-'));
    return entry ? entry[1] : null;
  };

  /**
   * Sends an event to GA4, retrying for a while if the Google tag has not loaded yet.
   * @param {string} name - Event name.
   * @param {number} [tries=0] - Attempts made so far.
   */
  const sendGa4 = (name, tries = 0) => {
    const id = ga4Id();
    if (typeof window.gtag === 'function' && id) {
      window.gtag('event', name, { send_to: id, transport_type: 'beacon' });
    } else if (tries < 15) {
      setTimeout(() => sendGa4(name, tries + 1), 1000);
    }
  };

  const isDismissed = () => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  const rememberDismissal = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch (e) {
      // Storage blocked: the bar will show again on the next page, which is acceptable.
    }
  };

  /**
   * Resolves to the visitor's detected ISO country code, or null.
   * @returns {Promise<?string>}
   */
  const detectCountry = async () => {
    const forced = new URLSearchParams(window.location.search).get('ksa_banner');
    if (forced) return forced.toUpperCase();

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return null;
    const data = await response.json();
    const country = data && data.detected_values && data.detected_values.country;
    return country && country.handle ? country.handle.toUpperCase() : null;
  };

  class KsaStoreBanner extends HTMLElement {
    connectedCallback() {
      if (isDismissed()) return;

      detectCountry()
        .then((country) => {
          if (country && country === (this.dataset.country || 'SA')) this.show();
        })
        .catch(() => {});
    }

    show() {
      this.hidden = false;
      sendGa4('ksa_banner_view');

      const link = this.querySelector('.js-ksa-banner-link');
      if (link) link.addEventListener('click', () => sendGa4('ksa_banner_click'));

      const close = this.querySelector('.js-ksa-banner-close');
      if (close) {
        close.addEventListener('click', () => {
          rememberDismissal();
          this.hidden = true;
        });
      }
    }
  }

  customElements.define('ksa-store-banner', KsaStoreBanner);
}
